<script setup lang="ts">
import { ref, computed, type Component } from 'vue'
import { usePageContext } from '@/composables/usePageContext'
import { useScreen } from '@/composables/useScreen'
import { useStaticAsset } from '@/composables/useStaticAsset'

const asset = useStaticAsset()

import LoginSigninForm from '@/ui-kit/login/SigninForm.vue'
import LoginSignupForm from '@/ui-kit/login/SignupForm.vue'
import LoginSignupProfileForm from '@/ui-kit/login/SignupProfileForm.vue'
import LoginRoleSelect from '@/ui-kit/login/RoleSelect.vue'
import LoginEmailVerification from '@/ui-kit/login/EmailVerification.vue'
import LoginCertificate from '@/ui-kit/login/Certificate.vue'
import LoginStudentCreate from '@/ui-kit/login/StudentCreate.vue'
import LoginRedirectLoader from '@/ui-kit/login/RedirectLoader.vue'
import LoginSmartbookStudentSelect from '@/ui-kit/login/smartbook/StudentSelect.vue'
import LoginSmartbookPurchase from '@/ui-kit/login/smartbook/Purchase.vue'
import LoginSmartbookSidebar from '@/ui-kit/login/smartbook/Sidebar.vue'
import LoginSeminarSidebar from '@/ui-kit/login/seminar/Sidebar.vue'
import HelpButton from '@/ui-kit/layout/HelpButton.vue'
import type { LoginPageContext } from '~/ui-kit/login/useLoginSection'

const LoginSection = {
  SigninForm: 'login-signin-form',
  SignupForm: 'login-signup-form',
  SignupProfileForm: 'login-signup-profile-form',
  RoleSelect: 'login-role-select',
  EmailVerification: 'login-email-verification',
  Certificate: 'login-certificate',
  StudentCreate: 'login-student-create',
  RedirectLoader: 'login-redirect-loader',
  SmartbookStudentSelect: 'login-smartbook-student-select',
  SmartbookPurchase: 'login-smartbook-purchase'
} as const

const sectionComponents: Record<string, Component> = {
  'login-signin-form': LoginSigninForm,
  'login-signup-form': LoginSignupForm,
  'login-signup-profile-form': LoginSignupProfileForm,
  'login-role-select': LoginRoleSelect,
  'login-email-verification': LoginEmailVerification,
  'login-certificate': LoginCertificate,
  'login-student-create': LoginStudentCreate,
  'login-redirect-loader': LoginRedirectLoader,
  'login-smartbook-student-select': LoginSmartbookStudentSelect,
  'login-smartbook-purchase': LoginSmartbookPurchase
}

const ctx = usePageContext<LoginPageContext>()
const { isMobile } = useScreen()

type LoginSectionValue = (typeof LoginSection)[keyof typeof LoginSection]
const currentSection = ref<LoginSectionValue>(LoginSection.SigninForm)
const query = new URLSearchParams(window.location.search)
const sharedData = ref<Record<string, unknown>>({
  legal: ctx.legal
})

const sectionData = computed(() => ({
  LoginSection,
  query,
  context: ctx,
  sharedData: sharedData.value
}))

const showSwitcher = computed(() => {
  if (query.get('switch_tabs') === 'false') return false
  return (
    [
      LoginSection.SigninForm,
      LoginSection.SignupForm,
      LoginSection.SignupProfileForm,
      LoginSection.RoleSelect
    ] as LoginSectionValue[]
  ).includes(currentSection.value)
})

const showStepper = computed(() => {
  if (query.has('purpose') && !isMobile.value) return false

  return !(
    [
      LoginSection.SigninForm,
      LoginSection.RoleSelect,
      LoginSection.RedirectLoader,
      LoginSection.SmartbookStudentSelect,
      LoginSection.SmartbookPurchase
    ] as LoginSectionValue[]
  ).includes(currentSection.value)
})

const signupStepsCount = computed(() => {
  let count = 4
  const isTeacher = query.get('type') === 'teacher' || sharedData.value.role === 1
  if (isTeacher && query.get('cert_step') === 'false') count--
  if (query.get('email_step') === 'false') count--
  if (query.get('create_step') !== 'true') count--
  if (count === 1) count = 0
  return count
})

const currentSignupStep = computed(() => {
  let step = 0
  switch (currentSection.value) {
    case LoginSection.SignupForm:
      step = 0
      break
    case LoginSection.SignupProfileForm:
      step = 1
      if (query.get('create_step') !== 'true') step -= 1
      break
    case LoginSection.EmailVerification:
      step = 2
      break
    case LoginSection.Certificate:
    case LoginSection.StudentCreate:
      step = 3
      break
  }
  return step
})

if (query.get('tab') === 'register') {
  currentSection.value = LoginSection.RoleSelect
}
if (query.get('purpose') === 'smartbook') {
  sharedData.value.role = 0
}
if (query.get('purpose') === 'seminar') {
  sharedData.value.role = 1
}
</script>

<template>
  <div class="login">
    <v-notifications position="bottom right" />
    <HelpButton category-override="login" />

    <div class="login__content">
      <div class="login__header">
        <a href="/" class="login__branding-logo">
          <v-logo :height="isMobile ? 40 : 64" />
        </a>

        <ul v-if="showStepper && !isMobile" class="login__signup-stepper">
          <li
            v-for="(_, index) in signupStepsCount"
            :key="index"
            class="login__signup-stepper-orb"
            :class="{ 'login__signup-stepper-orb_active': index === currentSignupStep }"
          />
        </ul>
        <template v-if="!showSwitcher" />

        <template v-else-if="isMobile">
          <v-button
            v-if="currentSection === LoginSection.SigninForm"
            mode="primary small flat"
            class="ml-2"
            @click="currentSection = LoginSection.RoleSelect"
          >
            Зарегистрироваться
          </v-button>
          <v-button v-else mode="primary small flat" class="ml-2" @click="currentSection = LoginSection.SigninForm">
            Войти в аккаунт
          </v-button>
        </template>

        <template v-else>
          <v-button
            v-if="currentSection === LoginSection.SigninForm"
            mode="primary flat"
            @click="currentSection = LoginSection.RoleSelect"
          >
            Зарегистрироваться
          </v-button>
          <v-button v-else mode="primary flat" @click="currentSection = LoginSection.SigninForm">
            Войти в свой аккаунт
          </v-button>
        </template>
      </div>

      <ul v-if="showStepper && isMobile" class="login__signup-stepper login__signup-stepper_mobile">
        <li
          v-for="(_, index) in signupStepsCount"
          :key="index"
          class="login__signup-stepper-orb"
          :class="{ 'login__signup-stepper-orb_active': index === currentSignupStep }"
        />
      </ul>

      <component
        :is="sectionComponents[currentSection]"
        v-bind="sectionData"
        @change="(s: LoginSectionValue) => (currentSection = s)"
        @update:shared-data="(d: Record<string, any>) => (sharedData = d)"
      />
    </div>

    <aside class="login__aside">
      <LoginSmartbookSidebar
        v-if="['smartbook', 'smartbook_grant'].includes(query.get('purpose') ?? '')"
        v-bind="sectionData"
        :current-section="currentSection"
        :grant-mode="query.get('purpose') === 'smartbook_grant'"
      />
      <LoginSeminarSidebar
        v-else-if="query.get('purpose') === 'seminar'"
        v-bind="sectionData"
        :current-section="currentSection"
      />
      <template v-else>
        <span class="login__aside-text mb-4"> Объединяем учителей, учеников и их родителей </span>
        <img :src="asset('img/login-img.png')" />
      </template>
    </aside>
  </div>
</template>

<style lang="scss">
@use '@/styles/pages/users/login.scss' as *;
@import url('https://fonts.googleapis.com/css2?family=Caveat&display=swap');
</style>
